import type { DeviceConfigurationApi } from '@model/DeviceConfiguration';
import {
  getSessionDomainKey,
  getSessionDomainLabel,
  getSessionTypeDisplay,
} from '../../../pages/sessions/utils/sessionTypeDisplay';
import type {
  PatientSessionDomainStat,
  PatientSessionListItem,
  PatientSessionsSummary,
} from '../types';

export const PATIENT_SESSION_STATUS_LABELS: Record<string, string> = {
  queued: 'Programmata',
  'in-progress': 'In corso',
  completed: 'Completata',
  initializing: 'Inizializzazione',
};

const ACTIVE_PATIENT_SESSION_STATUSES = new Set([
  'queued',
  'in-progress',
  'initializing',
]);

export type PatientSessionVideoRef = {
  id: number | string;
  category?: string | null;
  product_name?: string | null;
  name?: string | null;
};

export const createPatientSessionVideoLookup = (
  videos: PatientSessionVideoRef[]
): Map<number, PatientSessionVideoRef> => {
  const lookup = new Map<number, PatientSessionVideoRef>();

  videos.forEach(video => {
    const id = Number(video.id);
    if (Number.isFinite(id)) {
      lookup.set(id, video);
    }
  });

  return lookup;
};

export const resolvePatientSessionExperienceSource = (
  videoId: number | null | undefined,
  videoLookup: Map<number, PatientSessionVideoRef>
): string | null => {
  if (videoId == null || Number.isNaN(videoId)) {
    return null;
  }

  const video = videoLookup.get(videoId);
  if (video == null) {
    return null;
  }

  const category =
    typeof video.category === 'string' && video.category.trim().length > 0
      ? video.category.trim()
      : null;
  const productName =
    typeof video.product_name === 'string' &&
    video.product_name.trim().length > 0
      ? video.product_name.trim()
      : null;

  return category ?? productName ?? null;
};

const resolvePatientSessionDomain = (
  type: string,
  scenario: string | null,
  experienceSource: string | null
) => {
  const display = getSessionTypeDisplay(type, scenario, experienceSource);

  return {
    domainKey: getSessionDomainKey(type, scenario, experienceSource),
    domainLabel: getSessionDomainLabel(type, scenario, experienceSource),
    domainIconUrl: display.iconUrl,
  };
};

export const toPatientSessionList = (
  sessions: DeviceConfigurationApi[],
  patientId: number,
  videos: PatientSessionVideoRef[] = []
): PatientSessionListItem[] => {
  const videoLookup = createPatientSessionVideoLookup(videos);

  return sessions
    .filter(session => Number(session.patient_id ?? 0) === patientId)
    .map(session => {
      const type = String(session.type ?? '');
      const scenario =
        session.scenario == null || String(session.scenario).trim().length === 0
          ? null
          : String(session.scenario);
      const experienceSource = resolvePatientSessionExperienceSource(
        session.videoId ?? null,
        videoLookup
      );
      const domain = resolvePatientSessionDomain(
        type,
        scenario,
        experienceSource
      );

      return {
        id: Number(session.id),
        patientId:
          session.patient_id == null ? null : Number(session.patient_id),
        status: session.status,
        type,
        scenario,
        experienceSource,
        domainKey: domain.domainKey,
        domainLabel: domain.domainLabel,
        domainIconUrl: domain.domainIconUrl,
        deviceId: session.device_id == null ? null : String(session.device_id),
        deviceName: session.deviceName ?? null,
        patientCode: session.patientCode ?? null,
        createdAt: session.created_at ?? null,
        updatedAt: session.updated_at ?? null,
        startedAt: session.started_at ?? null,
        endedAt: session.ended_at ?? null,
      };
    })
    .sort((left, right) => {
      const leftTime = new Date(
        left.updatedAt ?? left.createdAt ?? left.startedAt ?? 0
      ).getTime();
      const rightTime = new Date(
        right.updatedAt ?? right.createdAt ?? right.startedAt ?? 0
      ).getTime();
      return rightTime - leftTime;
    });
};

export const summarizePatientSessionDomains = (
  sessions: PatientSessionListItem[]
): PatientSessionDomainStat[] => {
  const counts = new Map<string, PatientSessionDomainStat>();

  sessions.forEach(session => {
    const existing = counts.get(session.domainKey);
    if (existing) {
      existing.count += 1;
      return;
    }

    counts.set(session.domainKey, {
      domainKey: session.domainKey,
      label: session.domainLabel,
      iconUrl: session.domainIconUrl,
      count: 1,
    });
  });

  return Array.from(counts.values()).sort((left, right) => {
    if (right.count !== left.count) {
      return right.count - left.count;
    }

    return left.label.localeCompare(right.label, 'it');
  });
};

export const getLatestActivePatientSession = (
  sessions: PatientSessionListItem[]
): PatientSessionListItem | null =>
  sessions.find(session =>
    ACTIVE_PATIENT_SESSION_STATUSES.has(session.status)
  ) ?? null;

export const summarizePatientSessions = (
  sessions: PatientSessionListItem[]
): PatientSessionsSummary => {
  const queued = sessions.filter(session => session.status === 'queued').length;
  const inProgress = sessions.filter(
    session =>
      session.status === 'in-progress' || session.status === 'initializing'
  ).length;
  const completed = sessions.filter(
    session => session.status === 'completed'
  ).length;

  return {
    total: sessions.length,
    queued,
    inProgress,
    completed,
    latestSession: sessions[0] ?? null,
    activeSession: getLatestActivePatientSession(sessions),
    domains: summarizePatientSessionDomains(sessions),
  };
};
